import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import GlobalLayout from './GlobalLayout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { appConfig } from '../config'; 
import './CourseBuilder.css';
// fetchWithAuth entfernt — makeAuthenticatedRequest aus AuthContext übernimmt
// alle API-Calls inkl. automatischem Token-Refresh bei 401

// --- HELPER FUNCTIONS ---
const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
});

// --- SECURITY HELPER ---
const sanitizeInput = (str) => {
    if (!str) return '';
    // 1. HTML Tags entfernen (gegen XSS)
    let clean = str.replace(/<[^>]*>?/gm, '');
    // 2. Gefährliche SQL-Zeichen entschärfen (nur als Frontend-Vorfilter, Backend macht das eigentliche Escaping)
    // Wir lassen einfache Anführungszeichen zu (für Namen wie O'Connor), aber Backticks oder Semicolons in SQL-Manier filtern wir ggf.
    // Hier reicht meist der HTML-Strip + Trim für UX.
    return clean.trim();
};

const base64ToFile = async (base64String, fileName) => {
    const res = await fetch(base64String);
    const blob = await res.blob();
    return new File([blob], fileName, { type: "image/jpeg" });
};

const CourseBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, currentLanguage } = useLanguage();
  const { user, token, makeAuthenticatedRequest, features, hasFeature } = useAuth();

  // ── Zugriffskontrolle: features.course_builder ───────────────────────────
  useEffect(() => {
    if (!hasFeature('course_builder')) {
      navigate('/', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // ─────────────────────────────────────────────────────────────────────────
  
  // --- STEPS ---
  const [step, setStep] = useState(1);

  // --- QR HASH: Vorbelegter Hash aus einem Scan (z.B. von ElementFeed) ---
  // Wird als hash_id der neuen Collection übernommen (Backend: col-{hash})
  const [prefilledQRHash, setPrefilledQRHash] = useState(null);

  // --- STATE ---
  const [courseInfo, setCourseInfo] = useState({
    name: '',
    description: '',
    thumbnailFile: null,
    thumbnailPreview: null,
    xp: 100,
    level: '',
    is_challenge: false,
    is_mandatory: false 
  });

  const [availableElements, setAvailableElements] = useState([]);
  const [selectedElements, setSelectedElements] = useState([]);
  const [isLoadingElements, setIsLoadingElements] = useState(false);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [isLoadingLevels, setIsLoadingLevels] = useState(false);
  // --- NEU: CACHE STATE ---
  // Speichert Name & Beschreibung der letzten erfolgreichen KI-Abfrage
  const [lastAiParams, setLastAiParams] = useState({ name: '', description: '' });

  // --- NEU: AI SUGGESTION STATE ---
const [suggestedIds, setSuggestedIds] = useState([]);
const [isAiFilterActive, setIsAiFilterActive] = useState(false);
const [isLoadingAi, setIsLoadingAi] = useState(false);

  const [elementSearchQuery, setElementSearchQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [previewElement, setPreviewElement] = useState(null);
  const [createdCourse, setCreatedCourse] = useState(null);

  const dragItem = useRef();
  const dragOverItem = useRef();

  // --- PREFILLED QR HASH: Aus dem Router-State lesen (kommt von ElementFeed) ---
  useEffect(() => {
    const qrCode = location?.state?.prefilledQRCode;
    if (qrCode && typeof qrCode === 'string' && qrCode.trim().length > 0) {
      setPrefilledQRHash(qrCode.trim());
    }
  }, []); // Nur beim ersten Render auswerten

  // --- RESTORE DRAFT & NEW ELEMENT LOGIC ---
  useEffect(() => {
    // Helper: Restore Logic auslagern, damit wir sie nach dem Fetch aufrufen können
    const restoreDraft = (currentAvailableElements) => {
        const draft = sessionStorage.getItem('hejvi_course_draft');
        const newElementJson = sessionStorage.getItem('hejvi_created_element');

        if (!draft && !newElementJson) return currentAvailableElements;

        let updatedAvailable = [...currentAvailableElements];

        if (draft) {
            try {
                const parsed = JSON.parse(draft);
                
                // 1. Grunddaten wiederherstellen
                if (parsed.courseInfo) {
                    // THUMBNAIL WIEDERHERSTELLEN
                    const restoreThumbnail = async () => {
                        if (parsed.thumbnailBase64) {
                            try {
                                const file = await base64ToFile(parsed.thumbnailBase64, "restored_thumb.jpg");
                                setCourseInfo(prev => ({
                                    ...prev,
                                    ...parsed.courseInfo,
                                    thumbnailFile: file,
                                    thumbnailPreview: parsed.thumbnailBase64 // Base64 kann direkt als Source dienen
                                }));
                            } catch (e) {
                                console.error("Failed to restore thumbnail", e);
                            }
                        } else {
                             setCourseInfo(prev => ({ ...prev, ...parsed.courseInfo }));
                        }
                    };
                    restoreThumbnail();
                }
                
                // 2. Bisherige Elemente
                if (parsed.selectedElements) {
                    setSelectedElements(parsed.selectedElements);
                }
            } catch (e) {
                console.error("Error restoring draft", e);
            }
        }

        // 3. Das gerade erstellte Element hinzufügen
        if (newElementJson) {
            try {
                const newEl = JSON.parse(newElementJson);
                
                // Zu Selected hinzufügen (Duplikate vermeiden)
                setSelectedElements(prev => {
                    const exists = prev.find(e => String(e.id) === String(newEl.id));
                    return exists ? prev : [...prev, newEl];
                });

                // Zu Available hinzufügen (damit es im Grid erscheint und orange wird)
                // Wir prüfen, ob es schon im Grid ist (durch API Fetch). Wenn nicht, fügen wir es vorne an.
                const existsInAvail = updatedAvailable.find(e => String(e.id) === String(newEl.id));
                if (!existsInAvail) {
                    updatedAvailable = [newEl, ...updatedAvailable];
                }
                
                // Aufräumen
                sessionStorage.removeItem('hejvi_created_element');
                
                // Wir setzen den Step auf 2, damit man das Ergebnis sieht
                setStep(2);
                
            } catch (e) { console.error(e); }
        }

        // Draft löschen, aber Step Logic behalten
        if (draft) {
            setStep(2);
            sessionStorage.removeItem('hejvi_course_draft');
        }

        return updatedAvailable;
    };


    // 1. Levels laden
    fetchLevels();

    // 2. Elemente laden UND DANN Restore ausführen
    const loadData = async () => {
        setIsLoadingElements(true);
        try {
            const data = await makeAuthenticatedRequest('/elements/all', { method: 'GET' });
            
            let loadedElements = [];
            if (data && Array.isArray(data)) {
                loadedElements = data;
            }

            // HIER Restore aufrufen und mit den geladenen Daten mergen
            const finalElements = restoreDraft(loadedElements);
            setAvailableElements(finalElements);

        } catch (e) {
            console.error("Failed to load elements", e);
             // Auch bei Fehler versuchen, Draft zu laden (zumindest die lokalen Daten)
            const finalElements = restoreDraft([]);
            setAvailableElements(finalElements);
        } finally {
            setIsLoadingElements(false);
        }
    };

    loadData();

  }, []); // Empty dependency array -> runs once on mount

  
  const fetchLevels = async () => {
    setIsLoadingLevels(true);
    try {
      const langParam = currentLanguage || 'de';
      const data = await makeAuthenticatedRequest(`/levels?lang=${langParam}`, { method: 'GET' });
      if (data && Array.isArray(data)) {
        setAvailableLevels(data);
        // Nur Level setzen, wenn noch keins im Draft war (State checken wäre hier komplexer, 
        // aber da useEffect async läuft, lassen wir restoreDraft Vorrang haben)
        setCourseInfo(prev => {
            if(!prev.level && data.length > 0) return { ...prev, level: data[0].id };
            return prev;
        });
      }
    } catch (e) {
      console.error("Failed to load levels", e);
    } finally {
      setIsLoadingLevels(false);
    }
  };

  const handleInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCourseInfo(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 1. SICHERHEITSCHECK: Größe (Max 4MB)
    const maxSize = 4 * 1024 * 1024; // 4MB in Bytes
    if (file.size > maxSize) {
        setError(t('errorImageTooLarge') || 'Das Bild ist zu groß (Maximal 4MB).');
        e.target.value = null; // Input zurücksetzen
        return;
    }

    // 2. SICHERHEITSCHECK: Dateityp
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
        setError(t('errorInvalidImageType') || 'Ungültiges Format. Erlaubt sind: JPG, PNG, WEBP, GIF.');
        e.target.value = null; // Input zurücksetzen
        return;
    }

    // Wenn alles okay ist: Fehler löschen und Bild setzen
    setError('');
    setCourseInfo(prev => ({
      ...prev,
      thumbnailFile: file,
      thumbnailPreview: URL.createObjectURL(file)
    }));
  };

  const toggleElementSelection = (element) => {
    const isSelected = selectedElements.find(el => String(el.id) === String(element.id));
    if (isSelected) {
      setSelectedElements(prev => prev.filter(el => String(el.id) !== String(element.id)));
    } else {
      setSelectedElements(prev => [...prev, element]);
    }
  };

  // --- DRAG & DROP ---
  const dragStart = (e, position) => { dragItem.current = position; };
  const dragEnter = (e, position) => { dragOverItem.current = position; };
  const drop = (e) => {
    const copyListItems = [...selectedElements];
    const dragItemContent = copyListItems[dragItem.current];
    copyListItems.splice(dragItem.current, 1);
    copyListItems.splice(dragOverItem.current, 0, dragItemContent);
    dragItem.current = null;
    dragOverItem.current = null;
    setSelectedElements(copyListItems);
  };
  const removeElement = (index) => {
    const newList = [...selectedElements];
    newList.splice(index, 1);
    setSelectedElements(newList);
  };

  // --- NAVIGATION ---
  const handleNext = () => {
    // Validierung für Schritt 1
    if (step === 1) {
        // 1. Name ist Pflicht
        if (!courseInfo.name || !courseInfo.name.trim()) {
            setError(t('pleaseEnterName') || 'Bitte gib einen Kursnamen ein.');
            return;
        }

    }

    setError('');
    setStep(prev => prev + 1);
  };

  const handleBack = () => setStep(prev => prev - 1);

  // --- NEW: HANDLE EXTERNAL CREATION ---
  const handleCreateExternal = async (type) => {
      // 1. Thumbnail konvertieren (falls vorhanden)
      let thumbBase64 = null;
      if (courseInfo.thumbnailFile) {
          try {
             thumbBase64 = await fileToBase64(courseInfo.thumbnailFile);
          } catch(e) {
             console.warn("Could not convert thumbnail", e);
          }
      }
      const builderState = {
        step,
        courseInfo,
        selectedElements,
        availableLevels,
       
      };
      localStorage.setItem('courseBuilderState', JSON.stringify(builderState));

      // 2. Basis-Zustand definieren
      const draftBase = {
          courseInfo: {
              name: courseInfo.name,
              description: courseInfo.description,
              xp: courseInfo.xp,
              level: courseInfo.level,
              is_mandatory: courseInfo.is_mandatory,
              is_challenge: courseInfo.is_challenge // <--- WICHTIG: Auch den neuen Challenge-Flag mitnehmen
          },
          selectedElements: selectedElements
      };

      // 3. Versuchen zu speichern
      try {
        // Versuch A: Mit Bild speichern
        const fullDraft = { ...draftBase, thumbnailBase64: thumbBase64 };
        sessionStorage.setItem('hejvi_course_draft', JSON.stringify(fullDraft));
      } catch (e) {
          console.warn("Storage full with image? Trying without image.", e);
          
          // Versuch B: Fallback OHNE Bild, damit der Workflow nicht kaputt geht
          try {
              const lightDraft = { ...draftBase, thumbnailBase64: null };
              sessionStorage.setItem('hejvi_course_draft', JSON.stringify(lightDraft));
              
              // Hinweis an den User, dass das Bild zu groß war, aber es weitergeht
              alert(t('warningImageTooLarge') || "Das Bild war zu groß für den Zwischenspeicher. Der Kurs-Entwurf wurde gesichert, aber du musst das Bild nach der Rückkehr erneut auswählen.");
          } catch (e2) {
              // Wenn selbst Text nicht mehr reinpasst (sehr unwahrscheinlich), dann Fehler
              console.error("Storage critical", e2);
              alert(t('draftSaveError') || "Draft could not be saved. Storage full.");
              return; // Abbruch, da keine Rückkehr möglich wäre
          }
      }

      // 4. Navigieren
      if (type === 'video') {
        // HIER DIE ÄNDERUNG:
        navigate('/create-element', { state: { returnUrl: '/create-course' } });
      }
      if (type === 'quiz') {
          navigate('/create-quiz', { state: { returnUrl: '/create-course' } });
      }
  };

  const handleCreateAnother = () => {
    setCourseInfo({
      name: '',
      description: '',
      thumbnailFile: null,
      thumbnailPreview: null,
      xp: 100,
      level: '',
      is_mandatory: false
    });
    setSelectedElements([]);
    setElementSearchQuery(''); 
    setCreatedCourse(null);
    setError('');
    setIsProcessing(false);
    setStep(1); 
  };

  const handleSubmit = async () => {
    // Leere Collections sind erlaubt — Elemente können später hinzugefügt werden.
    setIsProcessing(true);
    try {
      const formData = new FormData();
      // --- SECURITY: INPUTS BEREINIGEN ---
      const cleanName = sanitizeInput(courseInfo.name);
      const cleanDesc = sanitizeInput(courseInfo.description);
      
      formData.append('name', courseInfo.name);
      formData.append('description', courseInfo.description);
      formData.append('source_language', currentLanguage || 'de');
      formData.append('xp', courseInfo.xp);
      formData.append('is_mandatory', courseInfo.is_mandatory ? 1 : 0);
      // --- NEU: TYPE LOGIK ---
      // Wenn Challenge aktiviert ist -> Typ 2, sonst Typ 1
      const courseType = courseInfo.is_challenge ? 2 : 1;
      formData.append('type', courseType);
      if (courseInfo.level) {
          formData.append('level', courseInfo.level);
      }

      // --- QR HASH: Vorbelegten Code als hash_id an das Backend übergeben ---
      // Das Backend generiert dann "col-{qr_hash}" als tatsächliche hash_id.
      if (prefilledQRHash) {
          formData.append('qr_hash', prefilledQRHash);
      }
      
      if (courseInfo.thumbnailFile) {
        formData.append('thumbnail', courseInfo.thumbnailFile);
      }
      
      const elementIds = selectedElements.map(el => el.id);
      formData.append('elements', JSON.stringify(elementIds));

      // makeAuthenticatedRequest übernimmt URL-Aufbau, Token-Refresh bei 401
      // und gibt pre-parsed JSON zurück — kein .json()-Aufruf nötig.
      const result = await makeAuthenticatedRequest('/create-course', {
        method: 'POST',
        body: formData,
      });

      // 409: Collection mit diesem QR-Code existiert bereits
      // makeAuthenticatedRequest wirft bei 4xx einen Error — 409 separat abfangen.
      if (result.status === 'exists' && result.hash_id) {
        navigate(`/${result.hash_id}`);
        return;
      }

      // --- ERROR HANDLING MIT ÜBERSETZUNG ---
      if (result.status === 'error') {
        let errorMsg = result.message || 'Server error';
        
        // Check auf Moderations-Fehler
        if (errorMsg.includes('Content rejected') || errorMsg.includes('moderation')) {
            errorMsg = t('errorContentRejected') || 'Dein Inhalt wurde abgelehnt, da er gegen die Sicherheitsrichtlinien verstößt (z.B. Beleidigung, Gewalt).';
        }

        throw new Error(errorMsg);
      }
      
      if (result.status === 'success') {
        setCreatedCourse(result); 
        setStep(4); 
      }
    } catch (e) {
      console.error("Submission error:", e);
      // 409: Collection mit QR-Code existiert bereits — e.data enthält hash_id
      if (e.status === 409 && e.data?.hash_id) {
        navigate(`/${e.data.hash_id}`);
        return;
      }
      if (e.status === 401 || e.message?.includes('401') || e.message === 'Unauthorized') {
        setError(t('authError401'));
      } else if (e.message?.includes('Content rejected') || e.message?.includes('moderation')) {
        setError(t('errorContentRejected'));
      } else {
        setError((t('uploadFailed') || 'Upload failed: ') + (e.message || String(e)));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // --- UPDATED: AI SUGGESTION HANDLER MIT CACHING ---
  const handleAiSuggest = async () => {
    // 1. Suchfeld leeren (wie gewünscht)
    setElementSearchQuery('');

    // 2. Toggle Logik: Wenn bereits aktiv, ausschalten
    if (isAiFilterActive) {
        setIsAiFilterActive(false);
        return;
    }

    // 3. Validierung
    if (!courseInfo.name || courseInfo.name.length < 3) {
        setError(t('pleaseEnterName') || 'Bitte gib im ersten Schritt einen Kursnamen ein.');
        return;
    }

    // --- NEU: CACHE CHECK ---
    // Haben wir schon Ergebnisse UND hat sich der Text NICHT geändert?
    const hasCachedResults = suggestedIds.length > 0;
    const isSameName = lastAiParams.name === courseInfo.name;
    const isSameDesc = lastAiParams.description === courseInfo.description;

    if (hasCachedResults && isSameName && isSameDesc) {
        // CACHE HIT: Wir nutzen die vorhandenen Daten
        console.log("Using cached AI suggestions"); // Optional für Debugging
        setIsAiFilterActive(true);
        return; // HIER STOPPEN (Kein API Call)
    }

    // --- API CALL (Cache Miss oder Daten geändert) ---
    setIsLoadingAi(true);
    setError('');

    try {
        const ids = await makeAuthenticatedRequest('/suggest-elements', {
            method: 'POST',
            body: JSON.stringify({
                name: courseInfo.name,
                description: courseInfo.description
            })
        });

        if (ids && Array.isArray(ids) && ids.length > 0) {
            setSuggestedIds(ids.map(id => Number(id)));
            
            // --- NEU: CACHE UPDATE ---
            // Wir merken uns, für welche Eingaben wir diese IDs bekommen haben
            setLastAiParams({
                name: courseInfo.name,
                description: courseInfo.description
            });
            
            setIsAiFilterActive(true);
        } else {
            alert(t('aiNoSuggestions') || "Die KI konnte keine spezifischen Elemente finden. Es werden alle angezeigt.");
            setIsAiFilterActive(false);
            // Bei leeren Ergebnissen löschen wir den Cache nicht zwingend, 
            // oder wir setzen suggestedIds auf leer. 
        }

    } catch (e) {
        console.error("AI Suggest Error:", e);
        setError("KI-Vorschlag fehlgeschlagen: " + e.message);
    } finally {
        setIsLoadingAi(false);
    }
  };


  // --- RENDER STEPS ---

  const renderStep1_Info = () => (
    <div className="cb-step fade-in">
      <h2>{t('courseDetails') || 'Course Details'}</h2>

      {/* INFO-BANNER: Wird angezeigt, wenn ein QR/EAN-Code aus dem Scanner übernommen wird */}
      {prefilledQRHash && (
        <div style={{
          background: 'rgba(255, 100, 7, 0.08)',
          border: '1px solid rgba(255, 100, 7, 0.3)',
          borderRadius: '10px',
          padding: '10px 14px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <span style={{ fontSize: '20px' }}>🔗</span>
          <div>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#FF6407', marginBottom: '2px' }}>
              {t('qrHashLinked') || 'Gescannter Code wird verknüpft'}
            </div>
            <div style={{ fontSize: '11px', color: '#555', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              col-{prefilledQRHash}
            </div>
          </div>
          <button
            onClick={() => setPrefilledQRHash(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '16px' }}
            title={t('remove') || 'Entfernen'}
          >✕</button>
        </div>
      )}

      <div className="cb-form-group cb-compact">
        <label>{t('courseName') || 'Course Name'}</label>
        <input 
          type="text" 
          name="name" 
          value={courseInfo.name} 
          onChange={handleInfoChange} 
          placeholder={t('courseNamePlaceholder') || "e.g. machine maintenance"}
        />
      </div>
      
      <div className="cb-form-group cb-compact">
        <label>{t('description') || 'Description'}</label>
        <textarea 
          name="description" 
          value={courseInfo.description} 
          onChange={handleInfoChange} 
          placeholder={t('courseDescPlaceholder') || "Briefly describe..."}
          rows={3} 
        />
      </div>

      <div className="cb-grid-row two-columns">
          <div className="cb-form-group cb-compact">
            <label>{t('level') || 'Level'}</label>
            <div className="cb-select-wrapper">
                <select 
                    name="level" 
                    value={courseInfo.level} 
                    onChange={handleInfoChange}
                    className="cb-select"
                >
                    <option value="">{t('select') || 'Select...'}</option>
                    {availableLevels.map(lvl => (
                        <option key={lvl.id} value={lvl.id}>
                            {lvl.name}
                        </option>
                    ))}
                </select>
                <div className="cb-select-arrow">▼</div>
            </div>
          </div>

          {/* RECHTE SPALTE: TOGGLES (Pflicht & Challenge) */}
          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
              
              {/* PFLICHT TOGGLE */}
              <div className="cb-form-group cb-compact cb-toggle-group">
                 <label style={{marginBottom: '4px'}}>{t('mandatory') || 'Pflicht?'}</label>
                 <label className="cb-switch">
                    <input 
                      type="checkbox" 
                      name="is_mandatory"
                      checked={courseInfo.is_mandatory}
                      onChange={handleInfoChange}
                    />
                    <span className="cb-slider round"></span>
                 </label>
              </div>

              {/* CHALLENGE TOGGLE (Nur Admin/Manager - Gruppe 0 oder 2) */}
              {hasFeature('content_management') && (
                  <div className="cb-form-group cb-compact cb-toggle-group">
                     <label style={{marginBottom: '4px', color: '#FF6407'}}>{t('challenge') || 'Aufgabe'}</label>
                     <label className="cb-switch">
                        <input 
                          type="checkbox" 
                          name="is_challenge"
                          checked={courseInfo.is_challenge}
                          onChange={handleInfoChange}
                        />
                        <span className="cb-slider round" style={{backgroundColor: courseInfo.is_challenge ? '#FF6407' : ''}}></span>
                     </label>
                  </div>
              )}

          </div>
      </div>

      <div className="cb-form-group cb-compact">
        <label style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
          {t('thumbnail') || 'Vorschaubild'}
          <span style={{
            fontSize: '10px', fontWeight: '500', color: '#9CA3AF',
            background: '#F3F4F6', borderRadius: '4px', padding: '1px 5px'
          }}>
            {t('optional') || 'optional'}
          </span>
        </label>
        <div className="cb-upload-area compact" onClick={() => document.getElementById('thumb-upload').click()}>
          {courseInfo.thumbnailPreview ? (
            <img src={courseInfo.thumbnailPreview} alt="Preview" className="cb-thumb-preview" />
          ) : (
            <div className="cb-upload-placeholder">
              <span style={{fontSize: '22px'}}>🖼️</span>
              <span style={{fontSize: '12px', textAlign: 'center', lineHeight: '1.4'}}>
                {t('uploadOrAutoThumb') || 'Bild hochladen'}
                <br/>
                <span style={{fontSize: '10px', color: '#9CA3AF'}}>
                  {t('autoThumbHint') || 'Sonst automatisch aus Videothumbnails'}
                </span>
              </span>
            </div>
          )}
          <input 
            id="thumb-upload" 
            type="file" 
            accept="image/*" 
            hidden 
            onChange={handleThumbnailChange}
          />
        </div>
      </div>
    </div>
  );

  const renderStep2_Selection = () => {
    // 1. Filtern und Ergebnis in einer Variable 'results' speichern
    let results = availableElements.filter(el => {
      // A) Text Suche
      const term = elementSearchQuery.toLowerCase();
      const matchesSearch = (
        (el.name && el.name.toLowerCase().includes(term)) ||
        (el.description && el.description.toLowerCase().includes(term))
      );

      // B) KI Filter
      // Wir vergleichen als Number, um Typ-Probleme (String vs Int) zu vermeiden
      const matchesAi = isAiFilterActive 
        ? suggestedIds.includes(Number(el.id)) 
        : true;

      return matchesSearch && matchesAi;
    });

    // 2. Sortieren (WICHTIG: Das muss VOR dem 'return' passieren!)
    // Wenn der KI-Filter aktiv ist, sortieren wir die Ergebnisse so, 
    // wie die KI sie geliefert hat (beste Treffer zuerst).
    if (isAiFilterActive && suggestedIds.length > 0) {
        results.sort((a, b) => {
            const indexA = suggestedIds.indexOf(Number(a.id));
            const indexB = suggestedIds.indexOf(Number(b.id));

            // Falls durch irgendeinen Zufall eine ID nicht gefunden wird, nach hinten schieben
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;

            return indexA - indexB; // Aufsteigend nach Index (0 = bester Treffer)
        });
    }

    // 3. Rendering
    return (
      <div className="cb-step fade-in">
        <h2>{t('addElements') || 'Add Elements'}</h2>
        
        <div className="cb-action-bar">
            <button className="cb-btn-action" onClick={() => handleCreateExternal('video')}>
                <span style={{fontSize:'20px'}}>📹</span> {t('createVideoElement') || 'Create Video'}
            </button>
            <button className="cb-btn-action" onClick={() => handleCreateExternal('quiz')}>
                <span style={{fontSize:'20px'}}>🧩</span> {t('createQuizElement') || 'Create Quiz'}
            </button>
        </div>

        <p className="cb-separator-text">{t('orSelectExisting') || 'Or select existing elements:'}</p>

        <p className="cb-subtitle">
            {selectedElements.length} {t('selected') || 'selected'} 
            {isAiFilterActive && <span style={{color: '#8b5cf6', marginLeft: '10px'}}>({t('filter')}: {results.length})</span>}
        </p>
        
        <div className="cb-form-group" style={{display: 'flex', gap: '10px'}}>
            <input
              type="text"
              placeholder={t('searchElements') || "Search..."}
              value={elementSearchQuery}
              onChange={(e) => setElementSearchQuery(e.target.value)}
              className="cb-search-input"
              style={{flex: 1}}
            />
            
            <button 
                type="button"
                onClick={handleAiSuggest}
                disabled={isLoadingAi}
                className="cb-btn-secondary"
                style={{
                    backgroundColor: isAiFilterActive ? '#4F46E5' : '#f3f4f6',
                    color: isAiFilterActive ? 'white' : '#1f2937',
                    border: isAiFilterActive ? '1px solid #4F46E5' : '1px solid #d1d5db',
                    minWidth: '160px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s'
                }}
            >
                {isLoadingAi ? (
                    <>
                       <span className="cb-btn-spinner" style={{width:'16px', height:'16px', borderWidth:'2px'}}></span>
                       {t('thinking') || 'Denke nach...'}
                    </>
                ) : isAiFilterActive ? (
                    <>
                       <span>❌</span> {t('showAll') || 'Alle anzeigen'}
                    </>
                ) : (
                    <>
                       <span>✨</span> {t('aiSuggest') || 'KI Vorschlag'}
                    </>
                )}
            </button>
        </div>

        {isLoadingElements ? (
          <div className="cb-loading">{t('loadingElements') || 'Loading...'}</div>
        ) : (
          <div className="cb-grid">
            {results.length === 0 && isAiFilterActive ? (
                <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: '#666'}}>
                    <p>{t('noElementsFound') || "Keine passenden Elemente gefunden."}</p>
                    <button 
                        className="cb-btn-outline" 
                        onClick={() => setIsAiFilterActive(false)}
                        style={{marginTop: '10px'}}
                    >
                        {t('showAll') || "Alle anzeigen"}
                    </button>
                </div>
            ) : (
                results.map(el => {
                  // ID Vergleich sicherstellen (String vs Int)
                  const isSelected = selectedElements.some(sel => String(sel.id) === String(el.id));
                  return (
                    <div key={el.id} className={`cb-card ${isSelected ? 'selected' : ''}`}>
                      <div className="cb-card-media" onClick={() => setPreviewElement(el)}>
                        {/* Bild mit Fallback, falls URL leer oder fehlerhaft */}
                        {el.url_thumbnail ? (
                            <img 
                                src={el.url_thumbnail} 
                                alt={el.name} 
                                onError={(e) => {
                                    e.target.style.display = 'none'; 
                                    e.target.parentNode.classList.add('no-image'); 
                                }}
                            />
                        ) : (
                            <div style={{
                                width: '100%', height: '100%', 
                                backgroundColor: '#e5e7eb', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '30px'
                            }}>📷</div>
                        )}
                        
                        <div className="cb-card-overlay">
                          <span>👁️</span>
                        </div>
                      </div>
                      <div className="cb-card-body">
                        <div className="cb-card-title">{el.name}</div>
                        <button 
                          className={`cb-btn-select ${isSelected ? 'active' : ''}`}
                          onClick={() => toggleElementSelection(el)}
                        >
                          {isSelected ? '✓' : '+'}
                        </button>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStep3_Structure = () => (
    <div className="cb-step fade-in">
      <h2>{t('organize') || 'Organize'}</h2>
      {/* --- Kursname anzeigen --- */}
      <div style={{
          background: '#F3F4F6', 
          border: '1px solid #E5E7EB', 
          borderRadius: '12px', 
          padding: '12px 16px', 
          margin: '0 0 20px 0', 
          textAlign: 'center'
      }}>
          <span style={{
              fontSize: '16px', 
              fontWeight: '700', 
              color: '#111827',
              display: 'block',
              lineHeight: '1.3'
          }}>
              {courseInfo.name}
          </span>
      </div>
      {selectedElements.length === 0 ? (
        /* Leerer-State: Collection ohne Elemente anlegen */
        <div style={{
          textAlign: 'center',
          padding: '32px 16px',
          background: 'rgba(255, 100, 7, 0.04)',
          border: '1px dashed rgba(255, 100, 7, 0.3)',
          borderRadius: '12px',
          marginBottom: '20px'
        }}>
          <div style={{ fontSize: '36px', marginBottom: '12px' }}>📂</div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
            {t('emptyCollectionHint') || 'Leere Collection'}
          </div>
          <div style={{ fontSize: '13px', color: '#6B7280', lineHeight: '1.5' }}>
            {t('emptyCollectionSubHint') || 'Elemente können nach dem Anlegen jederzeit hinzugefügt werden.'}
          </div>
        </div>
      ) : (
        <>
          <p className="cb-subtitle">{t('dragDropReorder') || 'Drag and drop to reorder'}</p>
          <div className="cb-sort-list">
            {selectedElements.map((el, index) => (
              <div 
                key={el.id} 
                className="cb-sort-item"
                draggable
                onDragStart={(e) => dragStart(e, index)}
                onDragEnter={(e) => dragEnter(e, index)}
                onDragEnd={drop}
                onDragOver={(e) => e.preventDefault()}
              >
                <div className="cb-drag-handle">☰</div>
                <img src={el.url_thumbnail || 'https://via.placeholder.com/50'} alt="" className="cb-mini-thumb"/>
                <div className="cb-item-info">
                  <span className="cb-item-index">{index + 1}.</span>
                  <span className="cb-item-name">{el.name}</span>
                </div>
                <button className="cb-btn-remove" onClick={() => removeElement(index)}>✕</button>
              </div>
            ))}
          </div>
        </>
      )}
      <div className="cb-add-more-container">
        <button className="cb-btn-outline" onClick={() => setStep(2)}>
          {selectedElements.length === 0
            ? (t('addElements') || '+ Elemente hinzufügen')
            : (t('addMoreElements') || '+ Add more')}
        </button>
      </div>
    </div>
  );

  const renderStep4_Success = () => {
    return (
    <div className="cb-step cb-success fade-in">
      <div className="cb-success-icon">🎉</div>
      <h2>{t('done') || 'Done!'}</h2>
      
      {createdCourse && createdCourse.qr_direct_url ? (
          <div style={{margin: '16px 0', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <img 
                  src={createdCourse.qr_direct_url} 
                  alt="QR Code" 
                  style={{
                      width: '160px', 
                      height: '160px', 
                      border: '1px solid #E5E7EB', 
                      borderRadius: '12px', 
                      padding: '8px', 
                      background: 'white'
                  }} 
              />
              <span style={{fontSize: '12px', color: '#6B7280', marginTop: '8px'}}>
                  {t('scanToStart') || 'Scan to start'}
              </span>
          </div>
      ) : null}

      <div className="cb-success-actions" style={{display: 'flex', flexDirection: 'column', gap: '10px', width: '100%'}}>
        
        {createdCourse && createdCourse.hash_id && (
            <button 
                className="cb-btn-success" 
                style={{width: '100%', padding: '14px', fontSize: '16px'}}
                onClick={() => navigate(`/course/${createdCourse.hash_id}`)}
            >
                {t('viewCourse') || 'Kurs ansehen'}
            </button>
        )}

        <div style={{display: 'flex', gap: '10px', width: '100%'}}>
            <button 
                className="cb-btn-primary" 
                style={{flex: 1}} 
                onClick={handleCreateAnother}
            >
                {t('createAnotherCourse') || 'New'}
            </button>
            
            <button 
                className="cb-btn-secondary" 
                style={{flex: 1}} 
                onClick={() => navigate('/dashboard')}
            >
                {t('backToDashboard') || 'Home'}
            </button>
        </div>
      </div>
    </div>
  );
  };

  return (
    <GlobalLayout>
      <div className="course-builder-container">
        <div className="cb-progress-container">
          {[1, 2, 3].map(s => (
            <div key={s} className={`cb-progress-step ${step >= s ? 'active' : ''}`}></div>
          ))}
        </div>

        {error && <div className="cb-error">{error}</div>}

        <div className="cb-content">
          {step === 1 && renderStep1_Info()}
          {step === 2 && renderStep2_Selection()}
          {step === 3 && renderStep3_Structure()}
          {step === 4 && renderStep4_Success()}
        </div>

        {step < 4 && (
          <div className="cb-footer">
            {step > 1 && (
              <button className="cb-btn-secondary" onClick={handleBack}>
                {t('back') || 'Back'}
              </button>
            )}
            
            {step < 3 ? (
              <button className="cb-btn-primary" onClick={handleNext}>
                {t('next') || 'Next'}
              </button>
            ) : (
              <button 
                className="cb-btn-success" 
                onClick={handleSubmit} 
                disabled={isProcessing}
                style={{minWidth: '120px'}}
              >
                {isProcessing ? (
                    <>
                        <span className="cb-btn-spinner"></span>
                        {t('saving') || 'Saving...'}
                    </>
                ) : (
                    t('create') || 'Create'
                )}
              </button>
            )}
          </div>
        )}

        {previewElement && (
           <div className="cb-modal-overlay" onClick={() => setPreviewElement(null)}>
             <div className="cb-modal">
               <button className="cb-modal-close" onClick={() => setPreviewElement(null)}>✕</button>
               <h3>{previewElement.name}</h3>
               <img src={previewElement.url_thumbnail} style={{width:'100%', borderRadius: '8px', marginTop:'10px'}} alt="" />
               <p style={{margin:'10px 0'}}>{previewElement.description}</p>
             </div>
           </div>
        )}
      </div>
    </GlobalLayout>
  );
};

export default CourseBuilder;